import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { SlabsData } from '@/services/pointConfiguration/pointConfiguration.types';
import { z } from 'zod';
import { useCreatePointSlab, useUpdatePointSlab } from '../points-configuration/hooks/usePointSlabMutations';
import { PointAgentTab } from '../points-configuration/pointConfiguration.constants';

const requiredMessage = 'common.form.validation.required';

const requiredNumber = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z
    .number({ required_error: requiredMessage, invalid_type_error: requiredMessage })
    .min(0, { message: requiredMessage }),
);

const optionalNumber = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z.number({ invalid_type_error: requiredMessage }).min(0, { message: requiredMessage }).optional(),
);

const requiredPointType = z.string().refine((value) => value !== '' && value !== 'default', {
  message: requiredMessage,
});

const createSchema = (activeTab: PointAgentTab) =>
  z
    .object({
      fromAmount: requiredNumber,
      toAmount: requiredNumber,
      pointType: requiredPointType,
      pointValue: requiredNumber,
      minValue: optionalNumber,
      maxValue: optionalNumber,
      pointType2: z.string().optional(),
      pointValue2: optionalNumber,
      minValue2: optionalNumber,
      maxValue2: optionalNumber,
      pointType3: z.string().optional(),
      pointValue3: optionalNumber,
      minValue3: optionalNumber,
      maxValue3: optionalNumber,
    })
    .superRefine((value, context) => {
      if (activeTab !== 'affiliate') return;

      const requireAffiliateFields = (
        pointType: string | undefined,
        pointValue: number | undefined,
        suffix: '2' | '3',
      ) => {
        if (!pointType || pointType === 'default') {
          context.addIssue({ code: z.ZodIssueCode.custom, path: [`pointType${suffix}`], message: requiredMessage });
        }
        if (pointValue === undefined) {
          context.addIssue({ code: z.ZodIssueCode.custom, path: [`pointValue${suffix}`], message: requiredMessage });
        }
      };

      requireAffiliateFields(value.pointType2, value.pointValue2, '2');
      requireAffiliateFields(value.pointType3, value.pointValue3, '3');
    })
    .refine((value) => value.toAmount >= value.fromAmount, {
      path: ['toAmount'],
      message: 'common.form.validation.invalid',
    });

const ManageSlabs = ({
  pointCategoryId,
  categoryName,
  activeTab,
  slab,
}: {
  pointCategoryId: string;
  categoryName?: string;
  activeTab: PointAgentTab;
  slab?: SlabsData;
}) => {
  const { t } = useDynamicLocale();
  const pointTypeOptions = [
    { label: t('configurationManagement.commissions.table.title.flat'), value: 'F' },
    { label: t('configurationManagement.commissions.table.title.percentage'), value: 'P', disabled: true },
  ];
  const form = useForm({
    schema: createSchema(activeTab),
    mode: 'onBlur',
    defaultValues: {
      fromAmount: slab?.fromAmount,
      toAmount: slab?.toAmount,
      pointType: slab?.pointType ?? 'default',
      pointValue: slab?.pointValue ?? slab?.value,
      minValue: slab?.minValue,
      maxValue: slab?.maxValue,
      pointType2: slab?.pointType2 ?? 'default',
      pointValue2: slab?.pointValue2 ?? undefined,
      minValue2: slab?.minValue2 ?? undefined,
      maxValue2: slab?.maxValue2 ?? undefined,
      pointType3: slab?.pointType3 ?? 'default',
      pointValue3: slab?.pointValue3 ?? undefined,
      minValue3: slab?.minValue3 ?? undefined,
      maxValue3: slab?.maxValue3 ?? undefined,
    },
  });
  const pointType = form.watch('pointType');
  const pointType2 = form.watch('pointType2');
  const pointType3 = form.watch('pointType3');
  const { mutateAsync: createSlab } = useCreatePointSlab();
  const { mutateAsync: updateSlab } = useUpdatePointSlab();
  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        const payload = {
          ...data,
          pointValue2: activeTab === 'affiliate' ? data.pointValue2 : 0,
          pointValue3: activeTab === 'affiliate' ? data.pointValue3 : 0,
          minValue: data.pointType === 'P' ? (data.minValue ?? 0) : 0,
          maxValue: data.pointType === 'P' ? (data.maxValue ?? 0) : 0,
          minValue2: data.pointType2 === 'P' ? (data.minValue2 ?? 0) : 0,
          maxValue2: data.pointType2 === 'P' ? (data.maxValue2 ?? 0) : 0,
          minValue3: data.pointType3 === 'P' ? (data.minValue3 ?? 0) : 0,
          maxValue3: data.pointType3 === 'P' ? (data.maxValue3 ?? 0) : 0,
        };

        if (slab) await updateSlab({ pointSlabId: slab.pointSlabId, ...payload });
        else await createSlab({ pointCategoryId, ...payload });
      }}
    >
      <div className="flex w-full flex-col gap-4">
        <Input
          name="categoryName"
          maxLength={50}
          label="pointManagement.page.form.manageSlabs.categoryName"
          value={categoryName ?? ''}
          forceRequired
          disabled
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            inputMode="numeric"
            type="text"
            onlyNumbers
            maxLength={10}
            label="pointManagement.page.form.manageSlabs.fromAmount"
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterAmount')}
            {...form.register('fromAmount')}
            forceRequired
          />
          <Input
            inputMode="decimal"
            type="text"
            onlyNumbers
            maxLength={10}
            label="pointManagement.page.form.manageSlabs.toAmount"
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterAmount')}
            {...form.register('toAmount')}
            forceRequired
          />
        </div>
        {activeTab === 'affiliate' && (
          <div className="-mt-1.5 flex items-center gap-3 text-sm font-bold">
            <span className="shrink-0">
              <Translation id="pointManagement.page.form.managePointSlab.affiliateA" />
            </span>
            <div className="flex-1 border-t" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="pointManagement.page.form.managePointSlab.pointType"
            options={pointTypeOptions}
            placeholder={t('common.form.placeholder.select')}
            forceRequired
            {...form.register('pointType')}
          />
          <Input
            inputMode="decimal"
            type="text"
            onlyNumbers
            maxLength={10}
            label="pointManagement.page.form.manageSlabs.value"
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterValue')}
            forceRequired
            {...form.register('pointValue')}
          />
          <Input
            inputMode="decimal"
            type="text"
            onlyNumbers
            maxLength={10}
            label="pointManagement.page.form.managePointSlab.minValue"
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMinValue')}
            disabled={pointType !== 'P'}
            {...form.register('minValue')}
          />
          <Input
            inputMode="decimal"
            type="text"
            onlyNumbers
            maxLength={10}
            label="pointManagement.page.form.managePointSlab.maxValue"
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMaxValue')}
            disabled={pointType !== 'P'}
            {...form.register('maxValue')}
          />
        </div>
        {activeTab === 'affiliate' && (
          <>
            <div className="-mt-1.5 flex items-center gap-3 text-sm font-bold">
              <span className="shrink-0">
                <Translation id="pointManagement.page.form.managePointSlab.affiliateB" />
              </span>
              <div className="flex-1 border-t" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="pointManagement.page.form.managePointSlab.pointType"
                options={pointTypeOptions}
                placeholder={t('common.form.placeholder.select')}
                forceRequired
                {...form.register('pointType2')}
              />
              <Input
                inputMode="decimal"
                type="text"
                onlyNumbers
                maxLength={10}
                label="pointManagement.page.form.managePointSlab.value"
                placeholder={t('configurationManagement.commissions.manageSlabsModal.enterValue')}
                forceRequired
                {...form.register('pointValue2')}
              />
              <Input
                inputMode="decimal"
                type="text"
                onlyNumbers
                maxLength={10}
                label="pointManagement.page.form.managePointSlab.minValue"
                placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMinValue')}
                disabled={pointType2 !== 'P'}
                {...form.register('minValue2')}
              />
              <Input
                inputMode="decimal"
                type="text"
                onlyNumbers
                maxLength={10}
                label="pointManagement.page.form.managePointSlab.maxValue"
                placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMaxValue')}
                disabled={pointType2 !== 'P'}
                {...form.register('maxValue2')}
              />
            </div>
            <div className="-mt-1.5 flex items-center gap-3 text-sm font-bold">
              <span className="shrink-0">
                <Translation id="pointManagement.page.form.managePointSlab.affiliateC" />
              </span>
              <div className="flex-1 border-t" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="pointManagement.page.form.managePointSlab.pointType"
                options={pointTypeOptions}
                placeholder={t('common.form.placeholder.select')}
                forceRequired
                {...form.register('pointType3')}
              />
              <Input
                inputMode="decimal"
                type="text"
                onlyNumbers
                maxLength={10}
                label="pointManagement.page.form.managePointSlab.value"
                placeholder={t('configurationManagement.commissions.manageSlabsModal.enterValue')}
                forceRequired
                {...form.register('pointValue3')}
              />
              <Input
                inputMode="decimal"
                type="text"
                onlyNumbers
                maxLength={10}
                label="pointManagement.page.form.managePointSlab.minValue"
                placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMinValue')}
                disabled={pointType3 !== 'P'}
                {...form.register('minValue3')}
              />
              <Input
                inputMode="decimal"
                type="text"
                onlyNumbers
                maxLength={10}
                label="pointManagement.page.form.managePointSlab.maxValue"
                placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMaxValue')}
                disabled={pointType3 !== 'P'}
                {...form.register('maxValue3')}
              />
            </div>
          </>
        )}
      </div>
    </Form>
  );
};

export default ManageSlabs;
